'use client'

import { useEffect } from 'react'
import { trackModelShowcaseInteraction } from '@/lib/analytics'

/**
 * Two things worth knowing about a report: that it was opened at all, and which
 * takes people click through to the canvas. Take links come in two forms — the
 * generated `take-ref` anchors inside the prose and the "open on canvas" link
 * under each featured clip — so both are caught by delegation on the document
 * rather than by wiring handlers onto ~200 anchors.
 */
export function Flux3ReportTracker({ slug }: { slug: string }) {
  useEffect(() => {
    // Effects run child-first, so on a direct page load this executes before
    // PostHogProvider's effect has called initPostHog() and the capture would
    // be silently dropped — for exactly the search/shared-link traffic these
    // pages exist for. A macrotask runs after every effect in this commit.
    const opened = setTimeout(() => {
      trackModelShowcaseInteraction({ model: 'flux-3', interaction: 'report_opened', target: slug })
    }, 0)

    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null
      const ref = el?.closest?.('a.take-ref, a[data-take-link]')
      if (!(ref instanceof HTMLAnchorElement)) return
      const take = ref.getAttribute('data-take-link') ?? ref.textContent?.trim() ?? ''
      trackModelShowcaseInteraction({ model: 'flux-3', interaction: 'take_link', target: `${slug}:${take}` })
    }

    document.addEventListener('click', onClick)
    return () => {
      clearTimeout(opened)
      document.removeEventListener('click', onClick)
    }
  }, [slug])

  return null
}
